/**
 * Event timeline card: video plays when card is in viewport, pauses when out (stays visible, no poster).
 * Supports multiple .event-timeline-card elements; each .event-timeline-card__video-wrap is independent.
 *
 * YouTube/Vimeo use shared embed helpers (origin + widget_referrer, referrer policy) to avoid Error 153 in Safari.
 */

import { appendYoutubeIframeFallback, appendVimeoEmbed } from '../utils/video-embeds.js';

const FALLBACK_IMAGE_URL = 'https://placehold.co/713x200';
const EMBED_CLASS = 'event-timeline-card__video event-timeline-card__video--embed';

/** Matches `.event-timeline__year` bottom fade: gap = wrapper.bottom − year.bottom in viewport. */
const YEAR_WRAPPER_BOTTOM_EARLY_PX = 48;
const YEAR_WRAPPER_BOTTOM_FADE_PX = 140;

/** Below this width (CSS “below 768”), years use display:none when hidden and fade in (opacity 0→1). */
const MOBILE_YEAR_BREAKPOINT_PX = 768;
const MOBILE_YEAR_HIDE_BELOW_OPACITY = 0.05;
const MOBILE_YEAR_FADE_MS = 380;

function clearMobileYearRevealStyles(year) {
  if (!year) {
    return;
  }
  year.style.display = '';
  year.style.visibility = '';
  year.style.transition = '';
  year.removeAttribute('data-year-mobile-reveal');
}

function applyYearOpacityMobile(year, opacity) {
  if (!year) {
    return;
  }
  if (opacity < MOBILE_YEAR_HIDE_BELOW_OPACITY) {
    year.style.display = 'none';
    year.style.visibility = 'hidden';
    year.style.opacity = '0';
    year.style.pointerEvents = 'none';
    year.style.transition = '';
    year.removeAttribute('data-year-mobile-reveal');
    return;
  }

  year.style.pointerEvents = '';
  year.style.visibility = 'visible';
  year.style.display = '';

  const needsIntro = !year.hasAttribute('data-year-mobile-reveal');

  if (needsIntro) {
    year.style.transition = 'none';
    year.style.opacity = '0';
    void year.offsetHeight;
    year.style.transition = `opacity ${MOBILE_YEAR_FADE_MS}ms ease`;
    year.setAttribute('data-year-mobile-reveal', '');
    requestAnimationFrame(() => {
      if (window.innerWidth >= MOBILE_YEAR_BREAKPOINT_PX) {
        return;
      }
      year.style.opacity = String(opacity);
    });
  } else {
    year.style.transition = `opacity ${MOBILE_YEAR_FADE_MS}ms ease`;
    year.style.opacity = String(opacity);
  }
}

function yearWrapperBottomProximityOpacity(wrapper, year) {
  if (!wrapper || !year) {
    return 1;
  }
  const wb = wrapper.getBoundingClientRect().bottom;
  const yb = year.getBoundingClientRect().bottom;
  const gap = wb - yb;
  if (gap <= YEAR_WRAPPER_BOTTOM_EARLY_PX) {
    return 0;
  }
  return Math.min(1, (gap - YEAR_WRAPPER_BOTTOM_EARLY_PX) / YEAR_WRAPPER_BOTTOM_FADE_PX);
}

function hasVideoSource(wrap) {
  if (!wrap) return false;
  const type = (wrap.dataset.videoType || 'mp4').toLowerCase();
  const src = (wrap.dataset.videoSrc || '').trim();
  const id = (wrap.dataset.videoId || '').trim();
  if (type === 'mp4') return !!src;
  if (type === 'youtube' || type === 'vimeo') return !!id;
  return false;
}

function playVideo(wrap) {
  if (!wrap) return;
  wrap.classList.add('is-playing');
  const type = (wrap.dataset.videoType || 'mp4').toLowerCase();
  const src = wrap.dataset.videoSrc || '';
  const id = wrap.dataset.videoId || '';
  const posterSrc = wrap.dataset.posterSrc || '';

  if (type === 'mp4' && src) {
    let video = wrap.querySelector('video');
    if (!video) {
      video = document.createElement('video');
      video.className = 'event-timeline-card__video';
      video.src = src;
      video.poster = posterSrc;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      wrap.appendChild(video);
    }
    video.play().catch(() => {});
  } else if ((type === 'youtube' || type === 'vimeo') && id) {
    let iframe = wrap.querySelector('iframe');
    if (!iframe) {
      iframe =
        type === 'youtube'
          ? appendYoutubeIframeFallback(wrap, id, EMBED_CLASS)
          : appendVimeoEmbed(wrap, id, EMBED_CLASS);
    } else {
      sendEmbedCommand(iframe, type, 'play');
    }
  }
}

function pauseVideo(wrap) {
  if (!wrap) return;
  const type = (wrap.dataset.videoType || '').toLowerCase();
  const id = wrap.dataset.videoId || '';
  if (type === 'mp4') {
    const video = wrap.querySelector('video');
    if (video) video.pause();
  } else if ((type === 'youtube' || type === 'vimeo') && id) {
    const iframe = wrap.querySelector('iframe');
    if (iframe) sendEmbedCommand(iframe, type, 'pause');
  }
}

function sendEmbedCommand(iframe, type, action) {
  if (!iframe || !iframe.contentWindow) return;
  try {
    if (type === 'youtube') {
      const msg = JSON.stringify({
        event: 'command',
        func: action === 'pause' ? 'pauseVideo' : 'playVideo',
        args: []
      });
      iframe.contentWindow.postMessage(msg, 'https://www.youtube.com');
    } else if (type === 'vimeo') {
      const msg = JSON.stringify({ method: action === 'pause' ? 'pause' : 'play' });
      iframe.contentWindow.postMessage(msg, 'https://player.vimeo.com');
    }
  } catch (_) {}
}

function setupPoster(wrap) {
  if (!wrap) return;
  let poster = wrap.querySelector('.event-timeline-card__poster');
  if (poster) {
    if (hasVideoSource(wrap)) {
      const posterSrc = wrap.getAttribute('data-poster-src');
      if (posterSrc) poster.src = posterSrc;
    }
    return;
  }
  poster = document.createElement('img');
  poster.className = 'event-timeline-card__poster';
  poster.src = wrap.getAttribute('data-poster-src') || FALLBACK_IMAGE_URL;
  poster.alt = '';
  poster.loading = 'lazy';
  wrap.appendChild(poster);
}

/**
 * Add/remove "active" on .event-timeline__cards-item:
 * - When a card's top hits the top of the viewport (within threshold), add "active" and keep it.
 * - Remove "active" from all cards only when the user scrolls back and reaches the top of the section (section top at viewport top).
 */
function setupScrollActiveState(section) {
  const cardItems = Array.from(section.querySelectorAll('.event-timeline__cards-item'));
  if (!cardItems.length) return;

  const TOP_THRESHOLD = 150; // px from viewport top – when a card's top is here or above, add active
  const SECTION_TOP_REACHED = 0; // remove active only when section top is at or above viewport top

  let ticking = false;
  function updateActiveState() {
    const sectionRect = section.getBoundingClientRect();
    const reachedTopOfSection = sectionRect.top >= SECTION_TOP_REACHED;

    if (reachedTopOfSection) {
      cardItems.forEach((el) => {
        el.classList.remove('active', 'is-previous');
      });
    } else {
      let lastActiveIndex = -1;

      // Only add active when a card hits the top; never remove here – removal happens only at section top
      cardItems.forEach((el, index) => {
        const r = el.getBoundingClientRect();
        if (r.top <= TOP_THRESHOLD && r.bottom > 0) el.classList.add('active');
        
        if (el.classList.contains('active')) {
          lastActiveIndex = index;
        }
      });

      const isMobile = window.innerWidth <= 767;

      cardItems.forEach((el) => {
        let hideDot = false;
        const dotContainer = el.querySelector('.event-timeline__dot');

        if (isMobile) {
          const wrapper = el.closest('.event-timeline__wrapper');
          const stickyCol = wrapper?.querySelector('.event-timeline__col--left');
          let stickyTopPx = 100;
          if (stickyCol) {
            const parsed = parseFloat(getComputedStyle(stickyCol).top);
            if (!Number.isNaN(parsed)) {
              stickyTopPx = parsed;
            }
          }

          if (wrapper) {
            const wrapperRect = wrapper.getBoundingClientRect();
            if (wrapperRect.top <= stickyTopPx) {
              if (stickyCol) {
                const stickyRect = stickyCol.getBoundingClientRect();
                if (dotContainer) {
                  // Measure the actual circle dot (not the container which includes the tall line)
                  const dotSpan = dotContainer.querySelector('.dot') || dotContainer;
                  const dotRect = dotSpan.getBoundingClientRect();

                  // Hide the dot if it has scrolled up past the bottom of the sticky text.
                  // We add a few pixels buffer (e.g. 10px) to let it smoothly slide under.
                  if (dotRect.top < stickyRect.bottom - 10) {
                    hideDot = true;
                  }
                }
              }
            }
          }

          if (dotContainer) {
            const containerRect = dotContainer.getBoundingClientRect();
            // Clip only from the top at the sticky/header offset. Large negative L/R/bottom
            // insets expand the clip box so the dot/line still paint when the abspos box has
            // ~0 shrink-to-fit width (only absolutely positioned children).
            if (containerRect.top < stickyTopPx) {
              const cropTop = stickyTopPx - containerRect.top;
              dotContainer.style.clipPath = `inset(${cropTop}px -9999px -9999px -9999px)`;
            } else {
              dotContainer.style.clipPath = 'none';
            }
          }
        } else {
          if (dotContainer) {
            dotContainer.style.clipPath = 'none';
          }
        }

        if (hideDot) {
          el.classList.add('is-previous');
        } else {
          el.classList.remove('is-previous');
        }
      });
    }
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateActiveState);
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  window.addEventListener('load', onScroll, { passive: true });
  updateActiveState();
}

/** Keeps layout when `.event-timeline__heading` is `position: fixed`. */
function ensureHeadingSpacer(heading) {
  let spacer = heading.previousElementSibling;
  if (!spacer || !spacer.classList.contains('event-timeline__heading-spacer')) {
    spacer = document.createElement('div');
    spacer.className = 'event-timeline__heading-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    spacer.style.cssText =
      'height:0;margin:0;padding:0;border:0;pointer-events:none;flex-shrink:0;';
    heading.before(spacer);
  }
  return spacer;
}

function resetHeadingStickyStyles(heading, spacer) {
  heading.style.position = '';
  heading.style.top = '';
  heading.style.left = '';
  heading.style.width = '';
  heading.style.boxSizing = '';
  heading.style.margin = '';
  heading.style.zIndex = '';
  heading.style.opacity = '';
  heading.style.pointerEvents = '';
  if (spacer) {
    spacer.style.height = '0px';
  }
}

/**
 * Fixed "sticky" heading only while scroll is between:
 * - first `.event-timeline__wrapper` top reaching the sticky offset (heading engages the timeline), and
 * - the last wrapper's bottom passing that stack (then release — same in reverse when scrolling up).
 * Opacity matches the last `.event-timeline__year` “end of block” fade (`yearWrapperBottomProximityOpacity`).
 */
function setupHeadingStickySectionFade(section) {
  const heading = section.querySelector('.event-timeline__heading');
  if (!heading) return;

  const leftCol = section.querySelector('.event-timeline__col--left');
  if (!leftCol) return;

  const wrappers = Array.from(section.querySelectorAll('.event-timeline__wrapper'));
  const firstWrapper = wrappers[0];
  const lastWrapper = wrappers[wrappers.length - 1];
  if (!firstWrapper || !lastWrapper) return;

  const lastYear = lastWrapper.querySelector('.event-timeline__year');
  const spacer = ensureHeadingSpacer(heading);
  const FADE_END_PX = 50;

  let ticking = false;
  function updateHeadingSticky() {
    const stickyTop = (() => {
      const t = parseFloat(getComputedStyle(leftCol).top);
      return Number.isNaN(t) ? 150 : t;
    })();

    const sectionRect = section.getBoundingClientRect();
    const hHeight = heading.offsetHeight;

    if (sectionRect.bottom < 0 || sectionRect.top > window.innerHeight) {
      resetHeadingStickyStyles(heading, spacer);
      ticking = false;
      return;
    }

    const firstTop = firstWrapper.getBoundingClientRect().top;
    const lastBottom = lastWrapper.getBoundingClientRect().bottom;

    const reachedFirstWrapper = firstTop <= stickyTop;
    const line = stickyTop + hHeight;
    const stillWithinLastWrapper = lastBottom > line;

    if (!reachedFirstWrapper || !stillWithinLastWrapper) {
      resetHeadingStickyStyles(heading, spacer);
      ticking = false;
      return;
    }

    const lr = leftCol.getBoundingClientRect();
    const endOpacity = lastYear
      ? yearWrapperBottomProximityOpacity(lastWrapper, lastYear)
      : Math.min(1, Math.max(0, (lastBottom - line) / FADE_END_PX));

    spacer.style.height = `${hHeight}px`;
    heading.style.position = 'fixed';
    heading.style.top = `${stickyTop}px`;
    heading.style.left = `${lr.left}px`;
    heading.style.width = `${lr.width}px`;
    heading.style.boxSizing = 'border-box';
    heading.style.margin = '0';
    heading.style.zIndex = '2';
    heading.style.opacity = String(endOpacity);
    heading.style.pointerEvents = endOpacity < 0.02 ? 'none' : '';

    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateHeadingSticky);
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  window.addEventListener('load', onScroll, { passive: true });
  updateHeadingSticky();
}

/**
 * Crossfade `.event-timeline__year` labels so only one reads as fully visible at a time:
 * when the next timeline wrapper scrolls up toward the sticky column, the previous year
 * fades out and the next fades in (same when scrolling back up).
 *
 * Each year also fades by proximity to its `.event-timeline__wrapper` top: full opacity until
 * the year sits within `YEAR_WRAPPER_MARGIN_PX` above the wrapper top, then ramps to 0.
 *
 * Fades out again before the wrapper bottom "catches" the year (sticky release): when
 * `wrapper.bottom - year.bottom` is small, opacity goes to 0 slightly early so the label
 * disappears before the visual end of the block.
 */
function setupYearCrossfade(section) {
  const pairs = Array.from(section.querySelectorAll('.event-timeline__wrapper'))
    .map((wrapper) => ({
      wrapper,
      year: wrapper.querySelector('.event-timeline__year'),
    }))
    .filter((pair) => pair.year);
  if (!pairs.length) return;

  const wrappers = pairs.map((pair) => pair.wrapper);
  const years = pairs.map((pair) => pair.year);

  const FADE_BAND_PX = 200;
  const YEAR_WRAPPER_MARGIN_PX = 20;

  function getStickyTopPx() {
    const leftCol = section.querySelector('.event-timeline__col--left');
    if (!leftCol) return 150;
    const t = parseFloat(getComputedStyle(leftCol).top);
    return Number.isNaN(t) ? 150 : t;
  }

  function wrapperProximityOpacity(wrapper, year) {
    const wt = wrapper.getBoundingClientRect().top;
    const yt = year.getBoundingClientRect().top;
    return Math.min(1, Math.max(0, (yt - (wt - YEAR_WRAPPER_MARGIN_PX)) / YEAR_WRAPPER_MARGIN_PX));
  }

  let ticking = false;
  function updateYearOpacities() {
    const isMobile = window.innerWidth < MOBILE_YEAR_BREAKPOINT_PX;
    const stickyTop = getStickyTopPx();
    const bandTop = stickyTop + FADE_BAND_PX;
    const n = years.length;

    const prox = pairs.map(({ wrapper, year }) => wrapperProximityOpacity(wrapper, year));
    const bottomProx = pairs.map(({ wrapper, year }) => yearWrapperBottomProximityOpacity(wrapper, year));

    const finalOpacities = new Array(n);
    if (n === 1) {
      finalOpacities[0] = prox[0] * bottomProx[0];
    } else {
      const p = [];
      for (let i = 0; i < n - 1; i++) {
        const nextTop = wrappers[i + 1].getBoundingClientRect().top;
        const t = Math.min(1, Math.max(0, (bandTop - nextTop) / FADE_BAND_PX));
        p.push(t);
      }
      finalOpacities[0] = (1 - p[0]) * prox[0] * bottomProx[0];
      for (let i = 1; i < n - 1; i++) {
        finalOpacities[i] = p[i - 1] * (1 - p[i]) * prox[i] * bottomProx[i];
      }
      finalOpacities[n - 1] = p[n - 2] * prox[n - 1] * bottomProx[n - 1];
    }

    if (isMobile) {
      years.forEach((year, i) => applyYearOpacityMobile(year, finalOpacities[i]));
      ticking = false;
      return;
    }

    years.forEach((year) => clearMobileYearRevealStyles(year));

    for (let i = 0; i < n; i++) {
      const o = finalOpacities[i];
      years[i].style.opacity = String(o);
      years[i].style.pointerEvents = o < 0.05 ? 'none' : '';
    }

    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateYearOpacities);
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  window.addEventListener('load', onScroll, { passive: true });
  updateYearOpacities();
}

export function eventTimeline() {
  const section = document.querySelector('.event-timeline');
  const cards = document.querySelectorAll('.event-timeline-card');
  if (!cards.length) return;

  if (section) {
    setupScrollActiveState(section);
    setupHeadingStickySectionFade(section);
    setupYearCrossfade(section);
  }

  cards.forEach((card) => {
    const videoWrap = card.querySelector('.event-timeline-card__video-wrap');
    if (!videoWrap) return;

    setupPoster(videoWrap);

    if (!hasVideoSource(videoWrap)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          playVideo(videoWrap);
        } else {
          pauseVideo(videoWrap);
        }
      },
      { threshold: 0.25, rootMargin: '0px' }
    );
    observer.observe(videoWrap);
  });
}
